import { Link, useLocation } from "@tanstack/react-router";
import { ShoppingCart, Menu, X, User, Shield } from "lucide-react";
import { useEffect, useState } from "react";
import { useCart } from "@/lib/cart";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import logo from "@/assets/logo.webp";

const NAV = [
  { to: "/", label: "Inicio" },
  { to: "/paquetes", label: "Paquetes" },
  { to: "/servicios", label: "Servicios" },
  { to: "/como-funciona", label: "Cómo funciona" },
  { to: "/faq", label: "FAQ" },
] as const;

export function Header() {
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const items = useCart((s) => s.items);
  const setCartOpen = useCart((s) => s.setOpen);
  const [hasSession, setHasSession] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  // Gate client-only state (zustand-persist + supabase session) to avoid
  // hydration mismatch (React #418): SSR renders with empty cart, client
  // hydrates from localStorage synchronously which would diverge.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
    let cancelled = false;
    async function checkAdmin(userId: string | undefined) {
      if (!userId) { if (!cancelled) setIsAdmin(false); return; }
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .eq("role", "admin")
        .maybeSingle();
      if (!cancelled) setIsAdmin(!!data);
    }
    const sub = supabase.auth.onAuthStateChange((_e, session) => {
      setHasSession(!!session);
      checkAdmin(session?.user?.id);
    });
    supabase.auth.getSession().then(({ data }) => {
      setHasSession(!!data.session);
      checkAdmin(data.session?.user?.id);
    });
    return () => { cancelled = true; sub.data.subscription.unsubscribe(); };
  }, []);

  return (
    <header className="sticky top-0 z-40 border-b border-hairline bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2 text-foreground">
          <img src={logo} alt="Truck Company Center" width={40} height={40} decoding="async" className="h-10 w-10 rounded-md object-contain" />
          <span className="font-display text-[15px] font-semibold tracking-tight">
            Truck <span className="text-primary">Company Center</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {NAV.map((n) => {
            const active = loc.pathname === n.to;
            return (
              <Link
                key={n.to}
                to={n.to}
                className={`px-3 py-2 text-sm transition-colors ${
                  active ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {n.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCartOpen(true)}
            className="relative"
            aria-label="Abrir carrito"
          >
            <ShoppingCart className="h-5 w-5" />
            {mounted && items.length > 0 && (
              <span className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-primary px-1 text-[11px] font-semibold text-primary-foreground">
                {items.length}
              </span>
            )}
          </Button>
          <Link to={hasSession ? "/cuenta" : "/cuenta/login"} className="hidden md:inline-flex">
            <Button variant="ghost" size="sm" aria-label="Mi cuenta">
              <User className="h-5 w-5" />
            </Button>
          </Link>
          {mounted && hasSession && isAdmin && (
            <Link to="/admin" className="hidden md:inline-flex">
              <Button
                variant="outline"
                size="sm"
                className="border-primary/40 bg-primary/10 text-primary hover:bg-primary/20 hover:text-primary"
                aria-label="Ir al panel admin"
              >
                <Shield className="h-4 w-4 mr-1.5" />
                Admin
              </Button>
            </Link>
          )}
          <Link to="/paquetes" className="hidden md:inline-flex">
            <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90">
              Ver paquetes
            </Button>
          </Link>
          <button
            className="md:hidden rounded-md p-2 text-foreground hover:bg-accent"
            onClick={() => setOpen((v) => !v)}
            aria-label="Abrir menú"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-hairline bg-background md:hidden">
          <nav className="mx-auto flex max-w-7xl flex-col px-4 py-2">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className="px-3 py-3 text-sm text-foreground hover:bg-accent rounded-md"
              >
                {n.label}
              </Link>
            ))}
            <Link to={hasSession ? "/cuenta" : "/cuenta/login"} onClick={() => setOpen(false)} className="px-3 py-3 text-sm text-foreground hover:bg-accent rounded-md">
              Mi cuenta
            </Link>
            {mounted && hasSession && isAdmin && (
              <Link
                to="/admin"
                onClick={() => setOpen(false)}
                className="px-3 py-3 text-sm font-medium text-primary hover:bg-primary/10 rounded-md inline-flex items-center gap-2"
              >
                <Shield className="h-4 w-4" />
                Panel Admin
              </Link>
            )}
            <Link to="/paquetes" onClick={() => setOpen(false)} className="mt-2">
              <Button size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
                Ver paquetes
              </Button>
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
